/* ============================================================
   Olivia & Mary — "Make your own gift box"

   1 to 4 items, a different product per item. Each item starts
   on its own product; a color is preselected, a size (or any
   other option that is not a color) is always left for the
   shopper to choose, so nobody gets a size picked for them.

   Adds everything in one request and then opens the cart drawer,
   or goes straight to checkout when Skip cart is on.
   ============================================================ */
(function () {
  'use strict';

  if (customElements.get('gift-box-bundle')) return;

  var COLOR_NAMES = ['color', 'colour', 'kleur'];

  function isColor(name) {
    return COLOR_NAMES.indexOf(String(name || '').toLowerCase().trim()) !== -1;
  }

  class GiftBoxBundle extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      // Back from checkout (straight-to-checkout mode): the browser can restore
      // this page from its back/forward cache with the button still busy.
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted || !this.redirectingToCheckout) return;
        this.redirectingToCheckout = false;
        this.setBusy(false);
      });

      try {
        this.data = JSON.parse(this.querySelector('[data-gift-data]').textContent);
      } catch (error) {
        return;
      }

      this.products = this.data.products.filter((product) => product.variants.some((variant) => variant.available));
      this.slots = Array.from(this.querySelectorAll('[data-gift-slot]'));
      this.tiers = Array.from(this.querySelectorAll('[data-bundle-tier]'));
      this.submitButton = this.querySelector('[data-bundle-submit]');
      this.buttonLabel = this.querySelector('[data-bundle-button-label]');
      this.totalElement = this.querySelector('[data-bundle-total]');
      this.errorElement = this.querySelector('[data-bundle-error]');
      this.stockElement = this.querySelector('[data-bundle-stock]');
      this.recentSlots = [];
      this.shownStock = {};
      this.quantity = Number((this.querySelector('[data-bundle-tiers] input:checked') || {}).value || 2);

      // Every item starts on its own product: the page's product, then the next ones.
      this.slots.forEach((slot, index) => {
        var product = this.products[index % this.products.length];
        if (product) this.chooseProduct(slot, product.id);
      });

      this.addEventListener('change', (event) => this.onChange(event));
      this.submitButton.addEventListener('click', () => this.addToCart());
      this.update();
    }

    productById(id) {
      return this.products.find((product) => String(product.id) === String(id)) || null;
    }

    onChange(event) {
      var tierInput = event.target.closest('[data-bundle-tier] input');
      if (tierInput) {
        this.quantity = Number(tierInput.value);
        this.update();
        return;
      }
      var slot = event.target.closest('[data-gift-slot]');
      if (!slot) return;
      this.recentSlots = [slot].concat(this.recentSlots.filter((other) => other !== slot));
      if (event.target.matches('.gift-box__product input')) {
        this.chooseProduct(slot, event.target.value);
      } else if (event.target.matches('[data-gift-options] select')) {
        this.resolve(slot);
        this.updateAvailability(slot);
      }
      this.update();
    }

    /* Show the photo as chosen and build this product's own option dropdowns. */
    chooseProduct(slot, productId) {
      var product = this.productById(productId);
      slot.product = product;
      Array.from(slot.querySelectorAll('.gift-box__product')).forEach((label) => {
        var input = label.querySelector('input');
        var chosen = !!product && input.value === String(product.id);
        input.checked = chosen;
        label.classList.toggle('is-selected', chosen);
      });

      var holder = slot.querySelector('[data-gift-options]');
      holder.replaceChildren();
      if (!product) return;

      var available = product.variants.filter((variant) => variant.available);
      if (!product.single_variant) {
        product.options.forEach((option, index) => {
          var select = document.createElement('select');
          select.dataset.optionIndex = String(index);
          select.setAttribute('aria-label', slot.querySelector('legend').textContent.trim() + ' — ' + option.name);
          var prompt = document.createElement('option');
          prompt.value = '';
          prompt.textContent = option.name;
          prompt.disabled = true;
          prompt.hidden = true;
          select.appendChild(prompt);
          option.values.forEach((value) => {
            var item = document.createElement('option');
            item.value = value;
            item.textContent = value;
            select.appendChild(item);
          });
          // A color starts on the first one in stock; a size is never chosen for the shopper.
          var start = isColor(option.name) && available[0] ? available[0].options[index] : '';
          select.value = start;
          if (isColor(option.name)) this.addColorPhotos(holder, select, product, option, index);
          holder.appendChild(select);
        });
      }
      this.resolve(slot);
      this.updateAvailability(slot);
    }

    /* With "1 item" the color is picked from variant photos instead of a
       dropdown. The dropdown stays the single source of the choice: a photo
       only sets its value. Shown only when the variants have photos. */
    addColorPhotos(holder, select, product, option, index) {
      var photos = option.values.map((value) => {
        var variant = product.variants.find((item) => item.options[index] === value && item.image);
        return { value: value, image: variant ? variant.image : null };
      });
      if (!photos.some((photo) => photo.image)) return;

      select.dataset.hasPhotos = 'true';
      var block = document.createElement('div');
      block.className = 'gift-box__colors';
      var label = document.createElement('p');
      label.className = 'gift-box__colors-label';
      label.append(option.name + ': ');
      var name = document.createElement('strong');
      name.dataset.colorName = '';
      label.appendChild(name);
      var row = document.createElement('div');
      row.className = 'gift-box__color-row';
      row.setAttribute('role', 'group');
      row.setAttribute('aria-label', option.name);
      photos.forEach((photo) => {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'gift-box__color';
        button.dataset.value = photo.value;
        button.title = photo.value;
        button.setAttribute('aria-label', photo.value);
        if (photo.image) {
          var img = document.createElement('img');
          img.src = photo.image;
          img.alt = '';
          img.width = 44;
          img.height = 44;
          img.loading = 'lazy';
          button.appendChild(img);
        } else {
          button.textContent = photo.value.slice(0, 1);
        }
        button.addEventListener('click', () => {
          if (button.disabled) return;
          select.value = photo.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });
        row.appendChild(button);
      });
      block.append(label, row);
      holder.appendChild(block);
    }

    syncColorPhotos(slot) {
      Array.from(slot.querySelectorAll('select[data-has-photos]')).forEach((select) => {
        var block = select.previousElementSibling;
        if (!block || !block.classList.contains('gift-box__colors')) return;
        var name = block.querySelector('[data-color-name]');
        if (name) name.textContent = select.value || '';
        Array.from(block.querySelectorAll('.gift-box__color')).forEach((button) => {
          var item = Array.from(select.options).find((option) => option.value === button.dataset.value);
          var chosen = select.value === button.dataset.value;
          button.classList.toggle('is-selected', chosen);
          button.setAttribute('aria-pressed', String(chosen));
          button.disabled = !!(item && item.disabled);
        });
      });
    }

    chosenOptions(slot) {
      return Array.from(slot.querySelectorAll('[data-gift-options] select')).map((select) => select.value || null);
    }

    /* `variant` is what gets bought and only exists once every option is set;
       `priceVariant` matches the choices so far and is used to show a price. */
    resolve(slot) {
      var product = slot.product;
      slot.variant = null;
      slot.priceVariant = null;
      if (!product) return;

      if (product.single_variant) {
        slot.variant = product.variants.find((variant) => variant.available) || null;
        slot.priceVariant = slot.variant || product.variants[0];
        return;
      }

      var wanted = this.chosenOptions(slot);
      var matches = (variant) => variant.options.every((value, index) => !wanted[index] || value === wanted[index]);
      if (wanted.every((value) => !!value)) {
        slot.variant = product.variants.find((variant) => variant.available && matches(variant)) || null;
      }
      slot.priceVariant =
        slot.variant ||
        product.variants.find((variant) => variant.available && matches(variant)) ||
        product.variants.find(matches) ||
        product.variants[0];

      Array.from(slot.querySelectorAll('[data-gift-options] select')).forEach((select) => {
        select.classList.toggle('is-unset', !select.value);
        if (select.value) select.classList.remove('is-missing');
      });
    }

    /* An option value is offered when a variant in stock carries it together
       with the other choices made so far. */
    updateAvailability(slot) {
      var product = slot.product;
      if (!product || product.single_variant) return;
      var wanted = this.chosenOptions(slot);
      Array.from(slot.querySelectorAll('[data-gift-options] select')).forEach((select) => {
        var optionIndex = Number(select.dataset.optionIndex);
        Array.from(select.options).forEach((item) => {
          if (item.value === '') return;
          item.disabled = !product.variants.some((variant) => {
            if (!variant.available || variant.options[optionIndex] !== item.value) return false;
            return variant.options.every(
              (value, index) => index === optionIndex || !wanted[index] || value === wanted[index]
            );
          });
        });
      });
      this.syncColorPhotos(slot);
    }

    discountFor(quantity) {
      return Number((this.data.discounts || {})[String(quantity)] || 0);
    }

    totalsFor(quantity) {
      var subtotal = this.slots.slice(0, quantity).reduce((sum, slot) => {
        var variant = slot.variant || slot.priceVariant;
        return sum + (variant ? Number(variant.price) : 0);
      }, 0);
      var saving = Math.round((subtotal * this.discountFor(quantity)) / 100);
      return { subtotal: subtotal, saving: saving, total: subtotal - saving };
    }

    formatMoney(cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: this.data.currency,
        }).format(cents / 100);
      } catch (error) {
        return (cents / 100).toFixed(2) + ' ' + this.data.currency;
      }
    }

    /* "1 item" is a plain purchase of the product on the page: no other
       products to pick from, the gift title swapped, a small nudge shown. */
    applySingleMode() {
      var single = this.quantity === 1;
      this.classList.toggle('is-single', single);
      var title = this.querySelector('[data-gift-title]');
      if (title) title.textContent = single ? title.dataset.single : title.dataset.multi;
      var note = this.querySelector('[data-gift-single-note]');
      if (note) note.hidden = !single;
      if (single && this.slots[0]) {
        var own = this.productById(this.data.page_product_id) || this.products[0];
        if (own && (!this.slots[0].product || this.slots[0].product.id !== own.id)) this.chooseProduct(this.slots[0], own.id);
      }
    }

    update() {
      this.applySingleMode();
      this.tiers.forEach((tier) => {
        var quantity = Number(tier.dataset.bundleTier);
        tier.classList.toggle('is-selected', quantity === this.quantity);
        var totals = this.totalsFor(quantity);
        var price = tier.querySelector('[data-tier-price]');
        var compare = tier.querySelector('[data-tier-compare]');
        var saving = tier.querySelector('[data-tier-saving]');
        var unit = tier.querySelector('[data-tier-unit]');
        if (price) price.textContent = this.formatMoney(totals.total);
        if (compare) {
          compare.textContent = this.formatMoney(totals.subtotal);
          compare.hidden = totals.saving === 0;
        }
        if (saving) {
          saving.hidden = totals.saving === 0;
          var value = saving.querySelector('span');
          if (value) value.textContent = this.formatMoney(totals.saving);
        }
        if (unit) unit.textContent = this.formatMoney(Math.round(totals.total / quantity)) + ' ' + (this.dataset.eachText || 'each');
      });

      this.slots.forEach((slot, index) => {
        slot.hidden = index >= this.quantity;
        var name = slot.querySelector('[data-gift-name]');
        if (name) {
          var variant = slot.variant || slot.priceVariant;
          name.textContent = slot.product ? slot.product.title + (variant ? ' · ' + this.formatMoney(Number(variant.price)) : '') : '';
        }
      });

      this.totalElement.textContent = this.formatMoney(this.totalsFor(this.quantity).total);
      this.clearError();
      this.updateStock();
    }

    /* Few-left line ([data-bundle-stock]). Shows a number from 1 to 8 per
       variant, drawn once per page view for each product, so going back to a
       size shows the same number. Sizes of one colour each get a different
       number. Deliberately not linked to Shopify inventory: the store keeps
       track of stock itself. */
    sizeIndex(product) {
      return product.options.findIndex((option) => !isColor(option.name) && /size/i.test(option.name));
    }

    drawShownStock(product) {
      var max = 8;
      var min = 1;
      var sizeIndex = this.sizeIndex(product);
      var groups = {};
      product.variants.forEach((variant) => {
        var key = variant.options.filter((value, index) => index !== sizeIndex).join('\u0000');
        (groups[key] = groups[key] || []).push(variant);
      });
      Object.keys(groups).forEach((key) => {
        var pool = [];
        for (var n = min; n <= max; n++) pool.push(n);
        for (var i = pool.length - 1; i > 0; i--) {
          var j = Math.floor(Math.random() * (i + 1));
          var swap = pool[i];
          pool[i] = pool[j];
          pool[j] = swap;
        }
        groups[key].forEach((variant, index) => {
          this.shownStock[variant.id] =
            index < pool.length ? pool[index] : min + Math.floor(Math.random() * (max - min + 1));
        });
      });
    }

    /* "Blue Woven" or "Blue Woven, size M": option values only, so no SKU,
       ID or "Default Title". Options with a single value are left out; any
       size option reads as "size". */
    variantName(product, variant) {
      if (product.single_variant) return '';
      var sizeIndex = this.sizeIndex(product);
      var names = [];
      var size = '';
      variant.options.forEach((value, index) => {
        var option = product.options[index];
        if (!value || !option || option.values.length < 2) return;
        if (index === sizeIndex) {
          size = 'size ' + value;
        } else {
          names.push(value);
        }
      });
      if (size) names.push(size);
      return names.join(', ');
    }

    /* The line follows the item the shopper chose last; until then the first
       item with a complete choice. It always names a variant that is in the
       box the button adds. */
    updateStock() {
      if (!this.stockElement) return;
      var element = this.stockElement;
      var visible = this.slots.slice(0, this.quantity);
      var order = this.recentSlots.filter((slot) => visible.indexOf(slot) !== -1).concat(visible);
      var slot = order.find((candidate) => candidate.product && candidate.variant && candidate.variant.available);

      if (!slot) {
        element.textContent = '';
        element.hidden = true;
        delete element.dataset.stockVariant;
        return;
      }

      var product = slot.product;
      var variant = slot.variant;
      if (!this.shownStock[variant.id]) this.drawShownStock(product);
      // Never fewer left than this box already holds of the same variant.
      var inBox = visible.filter((other) => other.variant && other.variant.id === variant.id).length;
      var count = Math.max(this.shownStock[variant.id], inBox);
      var name = this.variantName(product, variant);
      var fill = (template, fallback) =>
        String(template || fallback).replace(/\[count\]/g, count).replace(/\[variant\]/g, name);
      var text = !name
        ? fill(this.dataset.stockSingle, 'Only [count] left')
        : count === 1
          ? fill(this.dataset.stockOne, 'Only 1 left in [variant]')
          : fill(this.dataset.stockText, '[count] left in [variant]');

      var dot = document.createElement('span');
      dot.className = 'amelie-bundle__stock-dot';
      dot.setAttribute('aria-hidden', 'true');
      var label = document.createElement('span');
      label.textContent = text;
      element.replaceChildren(dot, label);
      element.dataset.stockVariant = String(variant.id);
      element.hidden = false;
    }

    setBusy(state) {
      this.busy = state;
      this.submitButton.disabled = state;
      this.submitButton.setAttribute('aria-busy', state ? 'true' : 'false');
      this.buttonLabel.textContent = state ? this.dataset.loadingText : this.dataset.buttonText;
    }

    showError(message) {
      this.errorElement.textContent = message || this.dataset.networkText;
      this.errorElement.hidden = false;
    }

    clearError() {
      this.errorElement.textContent = '';
      this.errorElement.hidden = true;
    }

    addToCart() {
      if (this.busy) return;
      this.clearError();

      var slots = this.slots.slice(0, this.quantity);
      if (slots.some((slot) => !slot.variant)) {
        this.showError(this.dataset.missingText);
        var open = [];
        slots.forEach((slot) => {
          Array.from(slot.querySelectorAll('[data-gift-options] select')).forEach((select) => {
            if (!select.value) open.push(select);
          });
        });
        open.forEach((select) => select.classList.add('is-missing'));
        if (open[0]) open[0].focus();
        return;
      }

      var grouped = {};
      slots.forEach((slot) => {
        var key = String(slot.variant.id);
        if (!grouped[key]) grouped[key] = { id: slot.variant.id, quantity: 0 };
        grouped[key].quantity += 1;
      });
      var items = Object.keys(grouped).map((key) => grouped[key]);
      var cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      var sections = cart && typeof cart.getSectionsToRender === 'function'
        ? cart.getSectionsToRender().map((section) => section.id)
        : [];
      var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';

      if (cart && typeof cart.setActiveElement === 'function') cart.setActiveElement(this.submitButton);
      this.setBusy(true);

      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: items, sections: sections, sections_url: window.location.pathname }),
      })
        .then((response) => response.json().then((payload) => ({ ok: response.ok, payload: payload })))
        .then((result) => {
          if (!result.ok || result.payload.status) {
            throw new Error(result.payload.description || result.payload.message || this.dataset.unavailableText);
          }
          if (window.oliviaSkipCart) {
            // Skip cart: the gift box is in the cart, go straight to checkout.
            this.redirectingToCheckout = true;
            window.location.href = '/checkout';
            return;
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-box-bundle', productVariantId: items[0].id, cartData: result.payload });
          }
          if (cart && typeof cart.renderContents === 'function') {
            cart.renderContents(result.payload);
          } else if (window.routes && window.routes.cart_url) {
            window.location.assign(window.routes.cart_url);
          }
        })
        .catch((error) => this.showError(error && error.message ? error.message : this.dataset.networkText))
        .finally(() => {
          if (!this.redirectingToCheckout) this.setBusy(false);
        });
    }
  }

  customElements.define('gift-box-bundle', GiftBoxBundle);
})();
